import type { ColDef } from 'ag-grid-enterprise'

import type { AccruedInterests } from '@fortem-monorepo/web-sdk'

import { getAccrualCheck } from '../accrual-check'
import { formatAccruedAmount } from '../amount-format'
import type { BalanceDisplayRow } from '../balance-drafts'
import { getBalanceStatus } from '../balance-status'
import { getCouponResetTags } from '../coupon-reset-tags'
import { compareDecimalAmounts } from '../decimal-sort'
import { sumDecimalAmounts } from '../decimal-sum'
import { AccrualCheckCell, RecentTradeCell } from './AccrualIndicatorCells'
import { BalanceStatusCell } from './BalanceStatusCell'
import { CouponResetCell } from './CouponResetCell'

export const centeredColumn: ColDef = {
  editable: false,
  sortable: true,
  filter: true,
  resizable: true,
  cellStyle: { textAlign: 'center' },
  headerClass: 'header-center',
  valueFormatter: ({ value }) => (value == null ? '—' : String(value)),
}

export const dailyColumns: ColDef<AccruedInterests.DailyRow>[] = [
  { field: 'trade_id', headerName: 'Trade', pinned: 'left', minWidth: 180 },
  { field: 'instrument_id', headerName: 'Instrument ID', width: 125, hide: true },
  { field: 'unique_identifier', headerName: 'Instrument', minWidth: 240, rowGroup: true, hide: true },
  { field: 'currency', headerName: 'Currency', width: 110, hide: true },
  { field: 'value_date', headerName: 'Date', width: 125 },
  { field: 'value_date_tmo', headerName: 'Reference date', width: 150 },
  {
    field: 'trade_daily_accrual_amount',
    headerName: 'Daily Accrual Local',
    aggFunc: ({ values }) => sumDecimalAmounts(values),
    minWidth: 190,
    comparator: compareDecimalAmounts,
    valueFormatter: ({ value }) => formatAccruedAmount(value),
    useValueFormatterForExport: false,
  },
  {
    field: 'past_5_day_average',
    comparator: compareDecimalAmounts,
    headerName: '5-day Avg Local',
    aggFunc: ({ values }) => sumDecimalAmounts(values),
    minWidth: 210,
    valueFormatter: ({ value }) => formatAccruedAmount(value),
    useValueFormatterForExport: false,
    headerTooltip:
      'Interest divided by covered calendar days for valid observations in the prior five calendar dates, scaled to this row’s accrual period. Friday-to-Monday covers three days. Unknown or pre-trade periods are excluded.',
  },
  { field: 'past_5_day_count', headerName: 'Average samples', width: 150, hide: true },
  {
    colId: 'accrual_check',
    headerName: 'Check',
    width: 155,
    valueGetter: ({ data }) =>
      data ? getAccrualCheck(data.trade_daily_accrual_amount, data.past_5_day_average) : null,
    valueFormatter: ({ value }) => value ?? '',
    cellRenderer: AccrualCheckCell,
    headerTooltip:
      'Absolute difference relative to the unrounded average: below 1% In Line; 1–5% Minor; above 5% Investigate. Zero average: zero accrual In Line, otherwise Investigate.',
  },
  {
    field: 'instrument_trade_count_3d',
    headerName: 'Trades Past 3d',
    cellRenderer: RecentTradeCell,
    valueFormatter: ({ value }) => (value > 0 ? 'NEW' : ''),
    useValueFormatterForExport: false,
    width: 190,
    headerTooltip:
      'Distinct active trades booked in this strategy/instrument on the selected date and previous two calendar dates (UTC).',
  },
  { field: 'quality_flag', headerName: 'Quality', width: 145 },
  { field: 'notes', headerName: 'Notes', minWidth: 260, tooltipField: 'notes' },
  { field: 'accrual_type', headerName: 'Accrual type', width: 145, hide: true },
  { field: 'trade_accrued_amount', headerName: 'Trade accrued', hide: true, comparator: compareDecimalAmounts },
  {
    field: 'trade_accrued_amount_tmo',
    headerName: 'Reference trade accrued',
    hide: true,
    comparator: compareDecimalAmounts,
  },
  { field: 'updated_by', headerName: 'Updated by', hide: true },
  { field: 'updated_datetime_utc', headerName: 'Updated at (UTC)', hide: true },
]

export const balanceColumns: ColDef<BalanceDisplayRow>[] = [
  { field: 'trade_id', headerName: 'Trade', pinned: 'left', minWidth: 180 },
  {
    colId: 'balance_status',
    headerName: 'State',
    valueGetter: ({ data }) => (data ? getBalanceStatus(data) : null),
    cellRenderer: BalanceStatusCell,
    minWidth: 180,
  },
  { field: 'instrument_id', headerName: 'Instrument ID', width: 125, hide: true },
  { field: 'unique_identifier', headerName: 'Instrument', minWidth: 240, rowGroup: true, hide: true },
  { field: 'currency', headerName: 'Currency', width: 110, hide: true },
  { field: 'value_date', headerName: 'Date', width: 125 },
  { field: 'previous_value_date', headerName: 'Prior saved date', width: 150 },
  {
    field: 'coupon_dates',
    headerName: 'Coupons ±2 days',
    minWidth: 250,
    cellRenderer: CouponResetCell,
    valueFormatter: ({ value, data, node }) =>
      getCouponResetTags(value, data?.value_date ?? node?.aggData?.value_date)
        .map((tag) => tag.label)
        .join('; '),
    headerTooltip: 'Scheduled coupon payments within two calendar days. This is an expected payment indicator.',
  },
  {
    field: 'previous_balance_local',
    headerName: 'Prior saved balance',
    minWidth: 185,
    comparator: compareDecimalAmounts,
    aggFunc: ({ values }) => sumDecimalAmounts(values),
    valueFormatter: ({ value }) => formatAccruedAmount(value),
    useValueFormatterForExport: false,
  },
  {
    field: 'opening_balance_local',
    headerName: 'Opening balance',
    minWidth: 185,
    comparator: compareDecimalAmounts,
    aggFunc: ({ values }) => sumDecimalAmounts(values),
    valueFormatter: ({ value }) => formatAccruedAmount(value),
    useValueFormatterForExport: false,
  },
  {
    field: 'trade_daily_accrual_amount',
    headerName: 'Daily Accrual Local',
    minWidth: 190,
    comparator: compareDecimalAmounts,
    valueFormatter: ({ value }) => formatAccruedAmount(value),
    useValueFormatterForExport: false,
  },
  {
    field: 'accrued_balance_local',
    headerName: 'Closing balance',
    minWidth: 185,
    comparator: compareDecimalAmounts,
    aggFunc: ({ values }) => sumDecimalAmounts(values),
    valueFormatter: ({ value }) => formatAccruedAmount(value),
    useValueFormatterForExport: false,
  },
  { field: 'notes', headerName: 'Notes', minWidth: 260, tooltipField: 'notes' },
  { field: 'updated_by', headerName: 'Updated by', hide: true },
  { field: 'updated_datetime_utc', headerName: 'Updated at (UTC)', hide: true },
]
